/**
 * Accessible 5-point scale selector (spec §8.7).
 *
 * Implemented as a native radiogroup: fully keyboard operable (arrow keys
 * move between options), visible Dutch text on every option, an icon in the
 * group legend, and a selected state that never relies on color alone
 * (the selected option shows a filled step indicator + stronger text).
 */
import { useId, type ReactNode } from 'react'
import type { ScaleOption } from './scaleOptions'

export type ScaleSelectorProps = {
  /** Fieldset legend, e.g. 'Stemming'. */
  legend: string
  /** Decorative group icon rendered next to the legend text. */
  icon: ReactNode
  name: 'mood' | 'energy' | 'stress'
  value: number
  onChange: (value: number) => void
  options: readonly ScaleOption[]
  disabled?: boolean
}

export function ScaleSelector({
  legend,
  icon,
  name,
  value,
  onChange,
  options,
  disabled = false,
}: ScaleSelectorProps) {
  const groupId = useId()
  return (
    <fieldset className="journal-scale" aria-disabled={disabled || undefined}>
      <legend className="journal-scale-legend" id={`${groupId}-legend`}>
        <span className="journal-scale-legend-icon" aria-hidden="true">
          {icon}
        </span>
        <span>{legend}</span>
      </legend>
      <div className="journal-scale-options" role="radiogroup" aria-labelledby={`${groupId}-legend`}>
        {options.map((option) => {
          const selected = option.value === value
          return (
            <label
              key={option.value}
              className={`journal-scale-option${selected ? ' journal-scale-option--selected' : ''}`}
            >
              <input
                type="radio"
                className="journal-scale-input"
                name={`journal-${name}`}
                value={option.value}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(option.value)}
              />
              <span className="journal-scale-indicator" aria-hidden="true">
                {option.value}
              </span>
              <span className="journal-scale-label">{option.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
